import { useState } from "react";
import {
  FiSearch,
  FiHelpCircle,
  FiMessageSquare,
  FiMail,
  FiPhone,
  FiClock,
  FiBookOpen,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiSend,
  FiFileText,
  FiHeadphones,
} from "react-icons/fi";

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] ${className}`}
    >
      {children}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-600 text-white shadow-sm">
        <Icon className="text-lg" />
      </div>
      <div>
        <h2 className="text-[18px] font-bold text-slate-800">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}

const faqItems = [
  {
    q: "How do I add a new container?",
    a: "Go to the Containers page, click Add Container, fill in the details, then save the record.",
  },
  {
    q: "How do I create a new invoice?",
    a: "Open the Invoices page and use the New Invoice button in the top right section.",
  },
  {
    q: "How do I update vendor information?",
    a: "Go to Vendors, search the vendor, then open edit from the action buttons on the right.",
  },
  {
    q: "How do I change dashboard settings?",
    a: "Open Settings from the sidebar and update profile, security, notifications, or billing options.",
  },
  {
    q: "How do I export reports?",
    a: "Open Reports and click Export from the top action bar to download your report file.",
  },
];

const supportChannels = [
  {
    title: "Email Support",
    text: "support@merakilogistics.com",
    icon: FiMail,
    color: "bg-blue-600",
  },
  {
    title: "Live Chat",
    text: "Available 24/7 for premium users",
    icon: FiMessageSquare,
    color: "bg-emerald-500",
  },
  {
    title: "Phone Support",
    text: "+44 20 1234 5678",
    icon: FiPhone,
    color: "bg-amber-500",
  },
];

const guides = [
  {
    title: "Getting Started Guide",
    desc: "Learn how to navigate the dashboard and manage your first records.",
    icon: FiBookOpen,
  },
  {
    title: "Billing & Invoices",
    desc: "Understand invoice creation, payment status, and export options.",
    icon: FiFileText,
  },
  {
    title: "System Status",
    desc: "Check if all services are running normally.",
    icon: FiCheckCircle,
  },
];

export default function Help() {
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState(0);
  const [message, setMessage] = useState("");

  const filteredFaq = faqItems.filter((item) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      item.q.toLowerCase().includes(q) ||
      item.a.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-[30px] font-bold tracking-tight text-slate-800">
            Help Center
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Find answers, contact support, and explore guides
          </p>
        </div>

        <div className="flex h-11 items-center gap-3 rounded-full border border-slate-200 bg-white px-4 shadow-sm">
          <FiSearch className="text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search help topics..."
            className="w-64 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Support Tickets</p>
              <h3 className="mt-2 text-[28px] font-bold text-slate-900">18</h3>
              <p className="mt-2 text-sm text-slate-500">3 open, 15 resolved</p>
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-600 text-white shadow-sm">
              <FiHeadphones className="text-lg" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Avg Response</p>
              <h3 className="mt-2 text-[28px] font-bold text-slate-900">2h</h3>
              <p className="mt-2 text-sm text-slate-500">Fast support turnaround</p>
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500 text-white shadow-sm">
              <FiClock className="text-lg" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Knowledge Base</p>
              <h3 className="mt-2 text-[28px] font-bold text-slate-900">24</h3>
              <p className="mt-2 text-sm text-slate-500">Articles available</p>
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-500 text-white shadow-sm">
              <FiBookOpen className="text-lg" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">System Status</p>
              <h3 className="mt-2 text-[28px] font-bold text-slate-900">Healthy</h3>
              <p className="mt-2 text-sm text-emerald-600 font-medium">
                All services online
              </p>
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500 text-white shadow-sm">
              <FiCheckCircle className="text-lg" />
            </div>
          </div>
        </Card>
      </div>

      {/* Main layout */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {/* Left side */}
        <div className="space-y-5 xl:col-span-2">
          {/* FAQ */}
          <Card className="p-5">
            <SectionTitle
              icon={FiHelpCircle}
              title="Frequently Asked Questions"
              subtitle="Quick answers to common questions"
            />

            <div className="mt-5 space-y-3">
              {filteredFaq.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 p-5 text-sm text-slate-500">
                  No help articles found for this search.
                </div>
              ) : (
                filteredFaq.map((item, index) => {
                  const open = openIndex === index;
                  return (
                    <button
                      key={item.q}
                      onClick={() => setOpenIndex(open ? -1 : index)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition hover:bg-slate-50"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-semibold text-slate-800">{item.q}</span>
                        {open ? (
                          <FiChevronDown className="text-slate-400" />
                        ) : (
                          <FiChevronRight className="text-slate-400" />
                        )}
                      </div>

                      {open && (
                        <p className="mt-3 text-sm leading-6 text-slate-500">
                          {item.a}
                        </p>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </Card>

          {/* Contact form */}
          <Card className="p-5">
            <SectionTitle
              icon={FiSend}
              title="Contact Support"
              subtitle="Send a message to the support team"
            />

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-600">
                  Name
                </label>
                <input
                  type="text"
                  defaultValue="John Smith"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-600">
                  Email
                </label>
                <input
                  type="email"
                  defaultValue="john@meraki.com"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-600">
                  Subject
                </label>
                <input
                  type="text"
                  defaultValue="Need help with invoices"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-600">
                  Message
                </label>
                <textarea
                  rows="5"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue clearly..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-4">
              <p className="text-sm text-slate-500">
                Average reply time: under 2 hours
              </p>

              <button className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-blue-700">
                <FiSend />
                Send Message
              </button>
            </div>
          </Card>
        </div>

        {/* Right side */}
        <div className="space-y-5">
          {/* Support channels */}
          <Card className="p-5">
            <SectionTitle
              icon={FiMessageSquare}
              title="Support Channels"
              subtitle="Choose the best way to contact us"
            />

            <div className="mt-5 space-y-3">
              {supportChannels.map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4"
                >
                  <div
                    className={`grid h-11 w-11 place-items-center rounded-xl ${item.color} text-white shadow-sm`}
                  >
                    <item.icon className="text-lg" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{item.title}</div>
                    <div className="mt-1 text-sm text-slate-500">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Guides */}
          <Card className="p-5">
            <SectionTitle
              icon={FiBookOpen}
              title="Helpful Guides"
              subtitle="Useful resources to get started"
            />

            <div className="mt-5 space-y-3">
              {guides.map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-600">
                      <item.icon className="text-lg" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{item.title}</div>
                      <div className="mt-1 text-sm text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Status */}
          <Card className="p-5">
            <SectionTitle
              icon={FiCheckCircle}
              title="Platform Status"
              subtitle="Current service health"
            />

            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between rounded-2xl bg-emerald-50 px-4 py-3">
                <span className="text-sm font-medium text-slate-700">API Status</span>
                <span className="text-sm font-semibold text-emerald-700">Operational</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-emerald-50 px-4 py-3">
                <span className="text-sm font-medium text-slate-700">Database</span>
                <span className="text-sm font-semibold text-emerald-700">Operational</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-emerald-50 px-4 py-3">
                <span className="text-sm font-medium text-slate-700">Notifications</span>
                <span className="text-sm font-semibold text-emerald-700">Operational</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}